/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description ExportEligibilityData button
 */
import React, { useMemo } from 'react';
import _ from 'lodash';
import { useLocation } from 'react-router-dom';
import { Alert, Button, Modal, Radio, Row, Col, Tag, Tooltip, Space, Spin, Flex, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Download } from '@icon-park/react';

import { RangePicker } from 'lib/antd';
import { VARIABLES } from 'styles/theme';
import { getSupportEmail } from '../DetailsRenderer';

import { getFilterParamsWithServiceDate } from 'components/eligibility/requests/EligibilityFilter';
import useAllEligibilityRequests, { getCoverageRule } from 'hooks/eligibility/useAllEligibilityRequests';
import useExportEligibilityData from 'hooks/eligibility/mutate/useExportEligibilityData';
import { addRuleToQuery } from 'utils/queryBuilder';
import { searchParamsKeys } from 'utils/router';
import { useAppStoreValues } from 'store/app';
import { customDatePresets } from 'utils/dateParser';
import { EXPORT_OPTIONS, dateType, dataKeyLabels } from 'utils/eligibility/eligibilityRequests';

// text style for header
const headerTextStyle = { fontSize: 14, fontWeight: 500 };

const MAX_EXPORT_ITEMS = 2000;

// text style for filter tags
const tagStyle = {
	borderStyle: 'dashed',
	borderColor: VARIABLES.PRIMARY_COLOR,
	padding: '3px 6px',
	marginBottom: 10,
	marginRight: 10,
	fontSize: 12,
};

// default pagination params
const paginationParams = {
	pageNumber: 1,
	pageSize: 1,
};

// Helper function to recursively get values by a specific key from a nested object
const getAllFilterKeysFromQuery = (ruleOrGroup: AnyType): string[] => {
	return _.transform(
		ruleOrGroup,
		(filterKeys: AnyType[], childRuleOrGroup: AnyType, key: string) => {
			if (key === 'field') {
				filterKeys.push(childRuleOrGroup);
			} else if (_.isObject(childRuleOrGroup)) {
				const nestedValues = getAllFilterKeysFromQuery(childRuleOrGroup);
				if (!_.isEmpty(nestedValues)) {
					filterKeys.push(...nestedValues);
				}
			}
		},
		[],
	);
};

// function to render filter tags
const renderFilterTags = (fieldValuesSet: string[]): React.ReactNode => {
	return (
		<Col span={24}>
			<Row className='flex'>
				<Col>
					<span style={headerTextStyle}>{'Filters Applied: '}</span>
				</Col>
				<Col className='ml-1.5'>
					{_.map(fieldValuesSet, (values, key) => {
						const displayedKey = _.get(dataKeyLabels, values) || key;
						return (
							<Tag style={tagStyle}>
								{displayedKey} {_.isArray(values) && _.size(values) ? ` (${_.size(values)})` : ''}
							</Tag>
						);
					})}
				</Col>
			</Row>
		</Col>
	);
};

// function that will return the service date component
const ServiceDateOptions: React.FC<IServiceDateComponentProps> = ({ disabled, exportMetaData, updateDateRange, isBatchResubmit }) => {
	const isRadio = exportMetaData?.dateRange?.key === dateType.radio;
	const isCustomDate = exportMetaData?.dateRange?.key === dateType.datePicker;
	return (
		<Flex gap={8} vertical>
			{isBatchResubmit && (
				<Typography.Text style={headerTextStyle}>
					{'Please select the range of service dates for which you want to resubmit the responses:'}
				</Typography.Text>
			)}
			<Radio.Group
				disabled={disabled}
				value={isRadio ? exportMetaData.dateRange?.range : null}
				onChange={(e) => updateDateRange({ key: dateType.radio, range: e.target.value })}
				className='flex flex-col gap-2'
			>
				{_.map(EXPORT_OPTIONS, (item) => (
					<Radio value={item.value} key={item.label}>
						{item.label}
					</Radio>
				))}
			</Radio.Group>
			<Radio onChange={() => updateDateRange({ key: dateType.datePicker, range: null })} checked={isCustomDate}>
				{'Custom date range'}
			</Radio>
			{isCustomDate && (
				<RangePicker
					value={isCustomDate ? exportMetaData.dateRange?.range : null}
					presets={customDatePresets}
					allowClear={false}
					disabled={disabled}
					onChange={(range) => {
						updateDateRange({ key: dateType.datePicker, range });
					}}
				/>
			)}
		</Flex>
	);
};

// jsx to return  the download warning
const DownloadWarning: React.FC = () => {
	const { setupInfo } = useAppStoreValues();
	return (
		<Col span={24} className='mx-0 my-auto'>
			<Alert
				message={
					<>
						{`You can only download up to ${MAX_EXPORT_ITEMS} responses at a time. To download more, please contact our support team:`}
						{getSupportEmail({ email: setupInfo?.supportEmail })}
						{'.'}
					</>
				}
				type='warning'
			/>
		</Col>
	);
};

// interface for service date component
interface IServiceDateComponentProps {
	exportMetaData: EligibilityRequests.IExportMeta;
	updateDateRange: (dateRange: EligibilityRequests.ISelectedDateMeta) => void;
	disabled?: boolean;
	isBatchResubmit?: boolean;
}

/**
 * ExportEligibilityData button
 */
export interface IProps {
	batchID?: string;
	storeValues: EligibilityRequests.IValues;
	storeActions: EligibilityRequests.IActions;
	patientRule?: AnyType;
	'data-testid'?: string;
}

const ExportEligibilityData: React.FC<IProps> = ({ batchID, storeValues, storeActions, patientRule, 'data-testid': dataTestId }) => {
	const { apiParams, exportMetaData } = storeValues;
	const { setExportMetaData, closeExportModal } = storeActions;
	const { mutate, isPending } = useExportEligibilityData();
	const { search } = useLocation();
	const coverageId = new URLSearchParams(search)?.get(searchParamsKeys.coverageId) || '';

	// prepare initial data based on api params
	const { filterParams, fieldValuesSet, hasFilterParams } = useMemo(() => {
		const params = _.pickBy(_.cloneDeep(apiParams), (value) => !_.isEmpty(value));
		const allFieldValues = _.uniq(getAllFilterKeysFromQuery(apiParams));
		return {
			filterParams: params,
			fieldValuesSet: allFieldValues,
			hasFilterParams: !_.isEmpty(allFieldValues),
		};
	}, [apiParams]);

	// prepare search params
	const searchParam = useMemo(() => {
		const base = {
			...(batchID && { batchId: batchID }),
			...(hasFilterParams && filterParams),
			...(!hasFilterParams && { criteria: getFilterParamsWithServiceDate(exportMetaData.dateRange.range) }),
		};
		const withPatientRule = patientRule
			? { ...base, criteria: { ...base.criteria, children1: [...(base.criteria?.children1 || []), patientRule] } }
			: base;
		// scope export to the coverage when viewing a coverage-specific list (mirrors useAllEligibilityRequests)
		return coverageId
			? { ...withPatientRule, criteria: addRuleToQuery(withPatientRule.criteria, getCoverageRule(coverageId)) }
			: withPatientRule;
	}, [filterParams, hasFilterParams, batchID, exportMetaData.dateRange.range, patientRule, coverageId]);

	// get the eligibility requests count freshly when the search param changes
	const {
		data: eligibilityData,
		isLoading,
		isFetching,
	} = useAllEligibilityRequests({
		apiParams: searchParam,
		pageNumber: paginationParams.pageNumber,
		pageSize: paginationParams.pageSize,
		options: { enabled: exportMetaData.visible && !!exportMetaData.dateRange?.range },
	});

	const eligibilityRequestCount = eligibilityData?.count && exportMetaData.dateRange?.range ? eligibilityData?.count : 0;
	const isLimitExceed = eligibilityRequestCount > MAX_EXPORT_ITEMS;

	// function to export eligibility data
	const exportData = () => {
		closeExportModal();
		mutate({ searchParam });
	};

	return (
		<>
			<Tooltip title={'Download Responses'}>
				<Button
					data-testid={dataTestId}
					color='default'
					loading={isPending}
					onClick={() => setExportMetaData({ ...exportMetaData, visible: true })}
					icon={<Download />}
				/>
			</Tooltip>

			<Modal
				open={exportMetaData?.visible}
				closable={true}
				maskClosable={false}
				width={450}
				title={'Download Responses'}
				okText={`Download Responses (${eligibilityRequestCount})`}
				onOk={exportData}
				onCancel={closeExportModal}
				cancelButtonProps={{ disabled: isPending }}
				okButtonProps={{
					loading: isPending,
					disabled: isLoading || isFetching || _.isEmpty(exportMetaData.dateRange?.range) || eligibilityRequestCount < 1,
				}}
			>
				{exportMetaData?.visible && (
					<Row gutter={[0, 10]}>
						{hasFilterParams && renderFilterTags(fieldValuesSet)}
						{!hasFilterParams && (
							<ServiceDateOptions
								exportMetaData={exportMetaData}
								updateDateRange={(dateRange) => setExportMetaData({ ...exportMetaData, dateRange })}
							/>
						)}
						{isLimitExceed && <DownloadWarning />}
						{(isLoading || isFetching) && (
							<Space>
								<Spin spinning indicator={<LoadingOutlined />} />
								{'Kindly wait while we process your request.'}
							</Space>
						)}
					</Row>
				)}
			</Modal>
		</>
	);
};

export {
	tagStyle,
	headerTextStyle,
	DownloadWarning,
	paginationParams,
	ServiceDateOptions,
	renderFilterTags,
	MAX_EXPORT_ITEMS,
	getAllFilterKeysFromQuery,
};

export default ExportEligibilityData;
